import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { User } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import './AdminUsers.css';

interface UsersResponse {
  users: (User & { orderCount?: number })[];
  totalCount: number;
}

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<(User & { orderCount?: number })[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);
  const { success, error: showError } = useToast();

  const fetchUsers = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<UsersResponse>('/admin/users', { params: { page, pageSize: 20 } });
      setUsers(response.data.users);
      setTotalCount(response.data.totalCount);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const updateUserRole = async (userId: number, newRole: string): Promise<void> => {
    setUpdatingUserId(userId);
    try {
      await api.put(`/admin/users/${userId}/role`, { role: newRole });
      fetchUsers();
      success('User role updated.');
    } catch (error) {
      showError('Failed to update user role.');
    } finally {
      setUpdatingUserId(null);
    }
  };

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="admin-users">
      <div className="container">
        <div className="page-header">
          <Link to="/admin" className="back-button">
            <i className="fas fa-arrow-left"></i> Back to Admin Panel
          </Link>
          <h1 className="page-title">User Management</h1>
        </div>

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Orders</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>#{user.id}</td>
                  <td>{user.email}</td>
                  <td>{user.firstName} {user.lastName}</td>
                  <td>
                    <select
                      value={user.role}
                      onChange={(e) => updateUserRole(user.id, e.target.value)}
                      className="role-select"
                      disabled={updatingUserId === user.id}
                    >
                      <option value="Customer">Customer</option>
                      <option value="Admin">Admin</option>
                    </select>
                  </td>
                  <td>{user.orderCount || 0}</td>
                  <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="btn btn-outline"
          >
            Previous
          </button>
          <span>Page {page} of {Math.ceil(totalCount / 20)}</span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= Math.ceil(totalCount / 20)}
            className="btn btn-outline"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
